import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../services/api';
import AdminShell from './AdminShell';

export default function AdminFounderMessagesPage() {
  const { user }               = useAuth();
  const navigate               = useNavigate();
  const { workspaceId }        = useParams();

  const [messages, setMessages]       = useState([]);
  const [workspace, setWorkspace]     = useState(null);
  const [loading, setLoading]         = useState(true);
  const [unreadOnly, setUnreadOnly]   = useState(false);
  const [composing, setComposing]     = useState(false);
  const [newText, setNewText]         = useState('');
  const [newAgent, setNewAgent]       = useState('');
  const [saving, setSaving]           = useState(false);

  useEffect(() => {
    if (user && !user.isSuperAdmin) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const crumbs = [
    { label: 'Dashboard',  href: '/admin' },
    { label: 'Workspaces', href: '/admin/workspaces' },
    { label: workspace?.name ?? workspaceId, href: `/admin/workspaces/${workspaceId}` },
    { label: 'Founder Messages' },
  ];

  const fetchMessages = () => {
    const qs = unreadOnly ? '?unread_only=true' : '';
    apiFetch(`/api/admin/workspaces/${workspaceId}/founder-messages${qs}`)
      .then(data => setMessages(data?.messages ?? []))
      .catch(() => setMessages([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setLoading(true);
    fetchMessages();
  }, [workspaceId, unreadOnly]);

  // Also fetch workspace name for breadcrumb
  useEffect(() => {
    apiFetch('/api/admin/workspaces')
      .then(data => {
        const ws = data?.workspaces?.find(w => w.id === workspaceId);
        if (ws) setWorkspace(ws);
      })
      .catch(() => {});
  }, [workspaceId]);

  const markRead = async (id) => {
    await apiFetch(`/api/admin/founder-messages/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ is_read: true }),
    });
    setMessages(prev => prev.map(m => m.id === id ? { ...m, is_read: true } : m));
  };

  const softDelete = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    await apiFetch(`/api/admin/founder-messages/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ deleted_at: new Date().toISOString() }),
    });
    setMessages(prev => prev.filter(m => m.id !== id));
  };

  const sendMessage = async () => {
    if (!newText.trim()) return;
    setSaving(true);
    try {
      const msg = await apiFetch(`/api/admin/workspaces/${workspaceId}/founder-messages`, {
        method: 'POST',
        body: JSON.stringify({ message_text: newText.trim(), focus_agent: newAgent.trim() || null }),
      });
      setMessages(prev => [msg, ...prev]);
      setNewText('');
      setNewAgent('');
      setComposing(false);
    } finally {
      setSaving(false);
    }
  };

  const unreadCount = messages.filter(m => !m.is_read).length;

  return (
    <AdminShell crumbs={crumbs}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start',
        justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: '28px',
            fontWeight: '400', marginBottom: '6px' }}>Founder Messages</h1>
          <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: 'var(--ink-mute)' }}>
            <span>Total: <strong style={{ color: 'var(--ink)' }}>{messages.length}</strong></span>
            <span>Unread: <strong style={{ color: unreadCount > 0 ? '#a0521a' : 'var(--ink)' }}>{unreadCount}</strong></span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <label style={{ fontSize: '13px', color: 'var(--ink-mute)',
            display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
            <input type="checkbox" checked={unreadOnly}
              onChange={e => setUnreadOnly(e.target.checked)} />
            Unread only
          </label>
          <button
            onClick={() => setComposing(c => !c)}
            style={{
              padding: '7px 16px', fontSize: '13px', fontWeight: '600',
              background: 'var(--ink)', color: 'var(--white)',
              border: 'none', borderRadius: 'var(--r)', cursor: 'pointer',
            }}
          >
            + New Message
          </button>
        </div>
      </div>

      {/* Compose panel */}
      {composing && (
        <div style={{
          background: 'var(--white)', border: '1px solid var(--paper-3)',
          borderRadius: 'var(--r)', padding: '20px', marginBottom: '20px',
        }}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px',
            fontFamily: 'var(--sans)' }}>New message from AI-CEO</h3>
          <textarea
            value={newText}
            onChange={e => setNewText(e.target.value)}
            placeholder="Message text…"
            rows={4}
            style={{
              width: '100%', padding: '10px 12px', fontSize: '13px',
              border: '1px solid var(--paper-3)', borderRadius: 'var(--r)',
              fontFamily: 'var(--sans)', resize: 'vertical',
              boxSizing: 'border-box', marginBottom: '10px',
            }}
          />
          <input
            value={newAgent}
            onChange={e => setNewAgent(e.target.value)}
            placeholder="Focus agent (optional)"
            style={{
              width: '100%', maxWidth: '300px', padding: '8px 12px', fontSize: '13px',
              border: '1px solid var(--paper-3)', borderRadius: 'var(--r)',
              fontFamily: 'var(--sans)', boxSizing: 'border-box', marginBottom: '14px',
              display: 'block',
            }}
          />
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={sendMessage} disabled={saving || !newText.trim()}
              style={{
                padding: '7px 18px', fontSize: '13px', fontWeight: '600',
                background: 'var(--ink)', color: 'var(--white)',
                border: 'none', borderRadius: 'var(--r)', cursor: 'pointer',
                opacity: saving || !newText.trim() ? 0.5 : 1,
              }}>
              {saving ? 'Sending…' : 'Send'}
            </button>
            <button onClick={() => setComposing(false)}
              style={{
                padding: '7px 18px', fontSize: '13px',
                background: 'transparent', color: 'var(--ink-mute)',
                border: '1px solid var(--paper-3)', borderRadius: 'var(--r)', cursor: 'pointer',
              }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <p style={{ color: 'var(--ink-mute)', fontSize: '13px' }}>Loading…</p>
      ) : messages.length === 0 ? (
        <p style={{ color: 'var(--ink-mute)', fontSize: '13px' }}>No messages found.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {messages.map(msg => (
            <div key={msg.id} style={{
              background: 'var(--white)', border: '1px solid var(--paper-3)',
              borderRadius: 'var(--r)', padding: '16px 20px',
              borderLeft: msg.is_read ? '3px solid transparent' : '3px solid #a0521a',
              opacity: msg.deleted_at ? 0.5 : 1,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'flex-start', gap: '12px' }}>
                <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.6',
                  color: 'var(--ink)', flex: 1, whiteSpace: 'pre-wrap' }}>
                  {msg.message_text}
                </p>
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  {!msg.is_read && (
                    <button onClick={() => markRead(msg.id)}
                      style={actionBtn('#2d8a6e')}>
                      Mark read
                    </button>
                  )}
                  <button onClick={() => softDelete(msg.id)}
                    style={actionBtn('var(--accent)')}>
                    Delete
                  </button>
                </div>
              </div>
              <div style={{ marginTop: '10px', display: 'flex', gap: '16px',
                fontSize: '11px', color: 'var(--ink-mute)', flexWrap: 'wrap' }}>
                <span>{new Date(msg.created_at).toLocaleString()}</span>
                {msg.focus_agent && (
                  <span style={{ background: 'var(--paper-2)', padding: '1px 8px',
                    borderRadius: '100px' }}>
                    agent: {msg.focus_agent}
                  </span>
                )}
                <span style={{
                  color: msg.is_read ? '#2d8a6e' : '#a0521a',
                  fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em',
                }}>
                  {msg.is_read ? 'Read' : 'Unread'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminShell>
  );
}

function actionBtn(color) {
  return {
    fontSize: '11px', padding: '3px 10px', borderRadius: 'var(--r)',
    border: `1px solid ${color}`, background: 'transparent',
    color, cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap',
  };
}